import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  styled,
  useMediaQuery,
} from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import Navigation from "../../../navigations/Navigation";
import TrackCard from "../../../components/TrackCard";
import { CallApiWithToken } from "../../../api/ApiRequest";
import { api_getReferral } from "../../../api/APIEndpoint";
import { getDisplayName } from "../../../utils/Utils";
import Loader from "../../../components/Loader";
import Alert from "../../../components/Alert";

function TrackScreen({ className }) {
  const [user, setUser] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width: 900px)");

  // Get the current user from Firebase
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser || null);
    });
    return () => unsubscribe();
  }, []);

  // Fetch referrals from API
  const getReferrals = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const res = await CallApiWithToken("POST", `${api_getReferral}`, {
        FullName: getDisplayName(user.displayName),
        PhoneNumber: user.phoneNumber?.slice(2) || "",
        Email: user.email,
      });

      const parsedResponse = typeof res === "string" ? JSON.parse(res) : res;
      if (parsedResponse?.Referrals) {
        setReferrals(parsedResponse.Referrals);
      } else {
        console.error("Unexpected response structure:", parsedResponse);
      }
    } catch (error) {
      console.error("API Error:", error);
      setAlert({
        open: true,
        message: "Failed to process the server response.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) getReferrals();
  }, [user]);

  // Handle Refresh
  const onRefresh = useCallback(() => {
    setRefreshing(true);
    getReferrals().finally(() => setRefreshing(false));
  }, [user]);

  return (
    <>
      {/* Static Header */}
      <Navigation />

      {/* Scrollable Content */}
      <div className={className}>
        <Box className="scrollableContent">
          <Box className="container">
            <Typography className="text">Your Referral Status</Typography>

            {isLoading ? (
              <Loader loading={isLoading} />
            ) : referrals.length > 0 ? (
              <List className="referralList">
                {referrals.map((item, index) => (
                  <ListItem key={index} className="referralItem">
                    <TrackCard item={item} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box className="emptyContainer">
                <Typography variant="h6" className="noHistory">
                  No referral history available.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </div>
      <Alert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert({ ...alert, open: false })}
      />
    </>
  );
}

export default styled(TrackScreen)(({}) => ({
  "*": {
    margin: 0,
    padding: 0,
    boxSizing: "border-box",
  },
  "html, body, #root": {
    height: "100%",
    overflow: "hidden",
  },
  ".scrollableContent": {
    position: "fixed",
    top: "64px", // Adjust based on the height of the navigation bar
    bottom: "56px", // Adjust based on the height of the bottom navigation
    left: 0,
    right: 0,
    overflowY: "auto",
    padding: "20px",
  },
  ".container": {
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  ".text": {
    fontWeight: 700,
    fontSize: "60px",
    letterSpacing: 0,
    color: "#234000",
    textAlign: "center",
    marginBottom: "20px",
    "@media (max-width: 900px)": {
      fontSize: "45px",
    },
    "@media (max-width: 600px)": {
      fontSize: "35px",
    },
  },
  ".referralList": {
    display: "grid",
    // gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
    width: "100%",
    maxWidth: "1000px",
    margin: "0 auto",
    padding: 0,
    "@media (max-width: 600px)": {
      // gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    },
  },
  ".referralItem": {
    display: "flex",
    justifyContent: "center",
    padding: "0",
  },
  ".emptyContainer": {
    backgroundColor: "rgba(220, 235, 186, 1)",
    borderRadius: "12px",
    padding: "20px",
    margin: "20px auto",
    maxWidth: "400px",
    textAlign: "center",
  },
  ".noHistory": {
    fontSize: "22px",
    textAlign: "center",
    fontWeight: 600,
  },
}));
